import { REACT_APP__BASE_PATH } from "@smp/utils";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useUpdateEffect } from "react-use";

import { IPolotnoTemplatesProps } from "@/components/features/polotno_templates";
import { queryParams } from "@/components/features/polotno_templates/helpers/query_params";

import { IContextPolotnoTemplates } from "./polotno_templates.types";

const ContextPolotnoTemplates = createContext<IContextPolotnoTemplates>(
  null as unknown as IContextPolotnoTemplates,
);

export const ProviderPolotnoTemplates = ({
  paramTemplateId,
  isRoutingDisabled,
  isWidget,
  onCreateNew,
  onOpen,
  onCopy,
  children,
  virtualizerTemplatesOptions,
  pathPattern,
  refs,
}: PropsWithChildren<
  Pick<IContextPolotnoTemplates["actions"], "onCreateNew"> &
    Pick<
      IContextPolotnoTemplates["state"],
      "isRoutingDisabled" | "isWidget" | "pathPattern"
    > &
    Pick<
      IPolotnoTemplatesProps,
      | "onOpen"
      | "onCopy"
      | "refs"
      | "virtualizerTemplatesOptions"
      | "paramTemplateId"
    >
>) => {
  const params = useParams<{ templateId?: string }>();
  const [activeEntity, setActiveEntity] = useState(
    paramTemplateId || params.templateId,
  );

  const initialQueryParams = useMemo(() => queryParams.deserialize(), []);

  const activeTemplateId = useMemo(
    () =>
      (activeEntity && !activeEntity.startsWith("unsplash:") && activeEntity) ||
      undefined,
    [activeEntity],
  );

  const activeUnsplashEntry = useMemo(() => {
    const unsplashEntry =
      activeEntity && activeEntity.startsWith("unsplash:")
        ? activeEntity
        : undefined;

    return (
      (unsplashEntry &&
        queryParams.deserializeUnsplashData(
          unsplashEntry.replace(/^unsplash:/g, ""),
        )) ||
      undefined
    );
  }, [activeEntity]);

  const [sortBy, setSortBy] =
    useState<IContextPolotnoTemplates["state"]["sortBy"]>("-created_at");

  const [selectedTags, setSelectedTags] = useState<
    IContextPolotnoTemplates["state"]["selectedTags"]
  >(initialQueryParams.tags);

  const [searchString, setSearchString] = useState<string>(
    initialQueryParams.search,
  );

  const [onlyFavorite, setOnlyFavorite] =
    useState<IContextPolotnoTemplates["state"]["onlyFavorite"]>(false);

  const changeOnlyFavorite: IContextPolotnoTemplates["actions"]["changeOnlyFavorite"] =
    () => {
      setOnlyFavorite((state) => !state);
    };

  const changeSortBy: IContextPolotnoTemplates["actions"]["changeSortBy"] = (
    option,
  ) => {
    setSortBy(option);
  };

  const updateSelectedTags = useCallback<
    IContextPolotnoTemplates["actions"]["updateSelectedTags"]
  >((parentSlug, slug, clearBefore) => {
    setSelectedTags((state) => {
      let newState = clearBefore
        ? []
        : state.filter((tag) => !tag.startsWith(parentSlug));

      if (slug && !state.includes(slug)) {
        newState.push(slug);
      }

      return newState;
    });
  }, []);

  const resetTags = () => setSelectedTags([]);

  const changeSearchString = (value: string) => {
    setSearchString(value);
  };

  const openInEditor: IContextPolotnoTemplates["actions"]["openInEditor"] = (
    data,
    isTargetBlank,
  ) => {
    const queryParam =
      typeof data === "string"
        ? data
        : `unsplash:${queryParams.serializeUnsplashData(data)}`;

    if (!isTargetBlank) {
      return onOpen(queryParam);
    } else if (REACT_APP__BASE_PATH) {
      const pathname = `/polotno/templates/${queryParam}`;
      const re = new RegExp(`^${REACT_APP__BASE_PATH}`);

      window.open(
        `${window.location.origin}${REACT_APP__BASE_PATH}${pathname.replace(
          re,
          "",
        )}`,
        "_blank",
      );
    }
  };

  const copyToEditor: IContextPolotnoTemplates["actions"]["copyToEditor"] =
    onCopy
      ? (data) => {
          const queryParam =
            typeof data === "string"
              ? data
              : `unsplash:${queryParams.serializeUnsplashData(data)}`;

          return onCopy(queryParam);
        }
      : undefined;

  const areSelectedTagsDirty = selectedTags.length > 0;

  useUpdateEffect(() => {
    setActiveEntity(paramTemplateId);
  }, [paramTemplateId]);

  useUpdateEffect(() => {
    setActiveEntity(params.templateId);
  }, [params.templateId]);

  return (
    <ContextPolotnoTemplates.Provider
      value={{
        actions: {
          updateSelectedTags,
          changeOnlyFavorite,
          changeSearchString,
          changeSortBy,
          setActiveEntity,
          onCreateNew,
          openInEditor,
          copyToEditor,
          resetTags,
        },
        state: {
          pathPattern,
          activeTemplateId,
          activeUnsplashEntry,
          areSelectedTagsDirty,
          isRoutingDisabled,
          isWidget,
          onlyFavorite,
          searchString,
          selectedTags,
          sortBy,
          virtualizerTemplatesOptions,
        },
        refs,
      }}
    >
      {children}
    </ContextPolotnoTemplates.Provider>
  );
};

export const useContextPolotnoTemplates = () =>
  useContext(ContextPolotnoTemplates);
